import { useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';

import { useAuth } from './auth/store.js';

/**
 * The application shell.
 *
 * The licence link lives in the footer of every page, which is the cheapest
 * way to satisfy "reachable without logging in" permanently rather than on
 * one page someone later moves behind auth.
 */
export function AppShell() {
  const status = useAuth((s) => s.status);
  const user = useAuth((s) => s.user);
  const refresh = useAuth((s) => s.refresh);
  const logout = useAuth((s) => s.logout);

  useEffect(() => {
    if (status === 'unknown') void refresh();
  }, [status, refresh]);

  return (
    // **A screen, not a page that grows.** The table is a play surface: its
    // transcript has to scroll under a header and side panels that stay put,
    // and with `min-h-screen` the whole document grew instead — scroll down to
    // read what just happened and the party list, the verb bar and the dice
    // scrolled away above you. Bounding the shell is what lets a page choose to
    // fill it; a page that is simply long still scrolls, inside `main`.
    <div className="h-screen flex flex-col bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <header className="border-b border-neutral-200 dark:border-neutral-800">
        <nav className="mx-auto max-w-5xl px-4 py-3 flex items-center gap-4">
          <Link to="/" className="font-semibold tracking-tight">
            Table Portal
          </Link>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {status === 'authenticated' && user && (
              <>
                {/* The setup guide. In the nav rather than on the campaign
                    page, because the moment it is needed most is before there
                    is a campaign to open. */}
                <Link to="/guide" className="underline underline-offset-2">
                  Guide
                </Link>
                <span className="text-neutral-600 dark:text-neutral-400">{user.displayName}</span>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="underline underline-offset-2"
                >
                  Sign out
                </button>
              </>
            )}
            {status === 'anonymous' && (
              <Link to="/login" className="underline underline-offset-2">
                Sign in
              </Link>
            )}
          </div>
        </nav>
      </header>

      {/* ⚠ **`relative` is what keeps this the ONLY scroll.** An `sr-only` label (position: absolute) inside a
          scrolling container that is not itself positioned belongs to the initial containing block instead: it
          sits at its in-flow offset inside `main`'s long content, which stretches `<html>` past the screen. The
          character sheet measured 189 px of document scroll (a second scrollbar, and the browser's own canvas —
          grey-blue in Firefox's dark mode — showing under the footer) from two such labels; Maz, 2026-10-21. */}
      {/* `min-h-0` is what lets this shrink below its content and scroll: a
          flex child defaults to `min-height: auto`, which is exactly why the
          transcript's own `overflow-y-auto` had never done anything.

          ⚠ **A `fillsTheShell` route test used to force `overflow-hidden`
          here, and it was compensating for nothing.** It was written during the
          session that misdiagnosed the transcript three times, and the two
          fixes that actually landed — `relative` on the transcript, so an
          `sr-only` label could not escape it and stretch `<html>`, and the
          closed `<details>` that was keeping its box — removed the cause
          underneath it. Measured rather than reasoned, because the previous
          three attempts were reasoned: `e2e/layout.spec.ts` swaps the class in
          a live browser at two viewport sizes and reads the same three boxes
          both ways. At 1280x720 and at 1024x600, `main` is 618/618 and 498/498
          — it has nothing to scroll at all — the transcript is identical, and
          `<html>` overflows by zero either way. So the rule is gone and the
          spec guards what it was for: the transcript scrolls, the document does
          not. */}
      <main className="relative min-h-0 flex-1 mx-auto w-full max-w-5xl overflow-y-auto px-4 py-8">
        <Outlet />
      </main>

      <footer className="border-t border-neutral-200 dark:border-neutral-800">
        <div className="mx-auto max-w-5xl px-4 py-4 text-sm text-neutral-600 dark:text-neutral-400">
          <Link to="/licence" className="underline underline-offset-2">
            Licence &amp; attribution
          </Link>
        </div>
      </footer>
    </div>
  );
}
